import API_BASE from "./apiConfig";
import React, { useState, useEffect } from "react";
import {
  FaKey, FaSave, FaTimesCircle, FaPlus, FaEdit, FaTrash,
  FaCheckCircle, FaExclamationCircle, FaShieldAlt,
  FaUser, FaRunning, FaLayerGroup, FaFileAlt, FaCog, FaLock, FaChild,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useTheme, getT } from "./ThemeContext";

const BASE = API_BASE;
const T = {
  glass:"rgba(255,255,255,0.72)", glassBorder:"1px solid rgba(255,255,255,0.55)",
  glassShadow:"0 8px 32px rgba(0,40,100,0.10), 0 1.5px 6px rgba(0,40,100,0.06)",
  blur:"blur(18px)", cardRadius:"18px", inputRadius:"10px",
  yellow:Colors.logoYellow, blue:Colors.brandBlue||"#034e7b",
  blueDark:Colors.primaryBlue||"#023a5c", iconGray:Colors.iconGray||"#8899aa",
  inputBg:"rgba(255,255,255,0.82)", font:"'Segoe UI', system-ui, -apple-system, sans-serif",
};
const labelSt=(T)=>({display:"block",marginBottom:7,fontSize:12,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",color:T.textLabel,fontFamily:T.font});
const inputWrapSt=(T)=>({display:"flex",alignItems:"center",border:`1.5px solid ${T.inputBdr}`,borderRadius:"10px",padding:"10px 14px",background:T.inputBg,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",width:"100%",boxSizing:"border-box"});
const inputSt=(T)=>({border:"none",outline:"none",width:"100%",background:"transparent",fontSize:14,color:T.inputText,fontFamily:T.font});
const glassCard=(T)=>({background:T.glass,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",border:T.glassBdr,borderRadius:"18px",boxShadow:T.glassSh,padding:"32px 36px",boxSizing:"border-box"});
const yellowBtn=(T)=>({padding:"11px 22px",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",color:"#02304f",display:"flex",alignItems:"center",justifyContent:"center",gap:8,backgroundImage:`linear-gradient(135deg,${T.yellow},#fff3a0)`,boxShadow:"0 4px 14px rgba(255,200,0,0.35)",fontFamily:T.font});
const blueBtn=(T)=>({padding:"11px 22px",border:`1px solid ${T.inputBdr}`,borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",color:T.inputText,display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:T.glass,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",boxShadow:"0 4px 14px rgba(0,0,0,0.25)",fontFamily:T.font});
const toastSt=(ok,T)=>({display:"flex",alignItems:"center",gap:10,padding:"13px 18px",borderRadius:10,marginBottom:20,fontSize:14,fontWeight:600,fontFamily:T.font,background:ok?"rgba(52,211,153,0.12)":"rgba(248,113,113,0.12)",color:ok?"#34d399":"#f87171",border:ok?"1px solid rgba(52,211,153,0.30)":"1px solid rgba(248,113,113,0.30)",backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)"});

function SectionLabel({children}){
  const { isDark } = useTheme();
  const T = getT(isDark);
  return(
    <div style={{display:"flex",alignItems:"center",gap:10,margin:"10px 0 16px"}}>
      <div style={{height:2,width:18,background:T.yellow,borderRadius:2}}/>
      <span style={{fontSize:11,fontWeight:800,letterSpacing:"0.10em",textTransform:"uppercase",color:T.textLabel,fontFamily:T.font}}>{children}</span>
      <div style={{flex:1,height:1,background:T.divider}}/>
    </div>
  );
}

// ── All pages exactly matching LandingPage.js navigation ─────────────────────
const PAGE_GROUPS = [
  {
    label: "Parent Portal",
    icon: FaChild,
    color: "#ec4899",
    pages: ["Performance Dashboard", "Metrics", "My Child"],
  },
  {
    label: "Executive",
    icon: FaFileAlt,
    color: "#1e3a8a",
    pages: ["Performance Intelligence", "Finance Dashboard"],
  },
  {
    label: "Students",
    icon: FaUser,
    color: "#3b82f6",
    pages: ["Student Master"],
  },
  {
    label: "Performance",
    icon: FaRunning,
    color: "#8b5cf6",
    pages: ["Performance Update", "Coach Attendance"],
  },
  {
    label: "Batches",
    icon: FaLayerGroup,
    color: "#10b981",
    pages: ["Batch Management", "Batch Upload", "Kiosk Check-In"],
  },
  {
    label: "Reports",
    icon: FaFileAlt,
    color: "#f59e0b",
    pages: [
      "Promotion Readiness",
      "Category Performance",
      "Performance Report",
      "Student Roster",
      "Attendance Report",
      "Progress Summary",
      "Performance Change Log",
      "Coach Evaluation",
    ],
  },
  {
    label: "Configuration",
    icon: FaCog,
    color: "#06b6d4",
    pages: ["Config Master"],
  },
  {
    label: "Security",
    icon: FaLock,
    color: "#ef4444",
    pages: ["Users", "Roles", "Fields Authorization"],
  },
];

const ALL_PAGES = PAGE_GROUPS.flatMap(g => g.pages);

const blank = ()=>({role_name:"",description:"",permissions:[]});

export default function RoleMaster({ currentUserId }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [roles,    setRoles]    = useState([]);
  const [form,     setForm]     = useState(blank());
  const [editId,   setEditId]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [msg,      setMsg]      = useState(null);

  const load = async()=>{
    try{ const r=await fetch(`${BASE}/roles/`); const d=await r.json(); setRoles(d.roles||[]); }catch{setRoles([]);}
  };
  useEffect(()=>{load();},[]);

  const openNew  = ()=>{ setEditId(null); setForm(blank()); setMsg(null); setShowForm(true); };
  const openEdit = (r)=>{ setEditId(r.role_id); setForm({role_name:r.role_name,description:r.description||"",permissions:r.permissions||[]}); setMsg(null); setShowForm(true); };

  const toggle = (p)=>setForm(prev=>{
    const has=prev.permissions.includes(p);
    return{...prev,permissions:has?prev.permissions.filter(x=>x!==p):[...prev.permissions,p]};
  });

  const toggleGroup = (pages)=>{
    const allOn = pages.every(p=>form.permissions.includes(p));
    if(allOn) setForm(prev=>({...prev,permissions:prev.permissions.filter(p=>!pages.includes(p))}));
    else setForm(prev=>({...prev,permissions:[...new Set([...prev.permissions,...pages])]}));
  };

  const toggleAll = ()=>{
    const allOn = ALL_PAGES.every(p=>form.permissions.includes(p));
    setForm(prev=>({...prev, permissions: allOn ? [] : [...ALL_PAGES]}));
  };

  const save = async()=>{
    if(!form.role_name.trim()){setMsg({ok:false,text:"Role name is required."});return;}
    const url=editId?`${BASE}/roles/${editId}`:`${BASE}/roles/`;
    try{
      const r=await fetch(url,{method:editId?"PUT":"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({...form,updated_by:currentUserId})});
      const d=await r.json();
      if(d.success){
        setMsg({ok:true,text:editId?"Role updated!":"Role created!"});
        load();
        setShowForm(false);
        window.dispatchEvent(new CustomEvent("rolesUpdated"));
      }
      else setMsg({ok:false,text:d.message||"Error"});
    }catch{setMsg({ok:false,text:"Network error"});}
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role.role_name}"? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${BASE}/roles/${role.role_id}`, {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updated_by: currentUserId }),
      });
      const d = await r.json();
      setMsg({ ok: d.success, text: d.message });
      if (d.success) { load(); window.dispatchEvent(new CustomEvent("rolesUpdated")); }
    } catch { setMsg({ ok: false, text: "Network error" }); }
  };

  const thSt={padding:"10px 14px",textAlign:"left",fontWeight:800,color:T.textPrimary,fontSize:11,letterSpacing:"0.07em",textTransform:"uppercase",fontFamily:T.font,borderBottom:`1.5px solid ${T.divider}`};
  const tdSt={padding:"10px 14px",borderBottom:T.rowBdr,color:T.textPrimary,fontSize:13,fontFamily:T.font};

  const allSelected = ALL_PAGES.every(p=>form.permissions.includes(p));
  const someSelected = form.permissions.length > 0 && !allSelected;

  return (
    <div style={{minHeight:"100%",padding:"20px 28px 48px",boxSizing:"border-box",fontFamily:T.font}}>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{width:42,height:42,borderRadius:12,flexShrink:0,background:`linear-gradient(135deg,${T.yellow},#fff3a0)`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 4px 14px rgba(255,200,0,0.32)"}}>
            <FaKey size={18} color={T.blue}/>
          </div>
          <div>
            <h2 style={{fontSize:22,fontWeight:800,color:T.textPrimary,margin:0,letterSpacing:"-0.01em"}}>Role Master</h2>
            <p style={{margin:0,fontSize:12,color:"#8899aa",marginTop:1}}>Define roles and their page-level access permissions</p>
          </div>
        </div>
        <button onClick={openNew} style={yellowBtn(T)}><FaPlus size={13}/> New Role</button>
      </div>

      {msg&&!showForm&&<div style={toastSt(msg.ok,T)}>{msg.ok?<FaCheckCircle/>:<FaExclamationCircle/>} {msg.text}</div>}

      {/* ── Form ── */}
      {showForm&&(
        <div style={{...glassCard(T),maxWidth:920,margin:"0 auto 24px"}}>
          {msg&&<div style={toastSt(msg.ok,T)}>{msg.ok?<FaCheckCircle/>:<FaExclamationCircle/>} {msg.text}</div>}
          <SectionLabel>{editId?"Edit Role":"Create New Role"}</SectionLabel>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"18px 28px",marginBottom:24}}>
            <div>
              <label style={labelSt(T)}>Role Name</label>
              <div style={inputWrapSt(T)}>
                <FaShieldAlt style={{marginRight:10,color:T.textMuted,flexShrink:0}}/>
                <input value={form.role_name} onChange={e=>setForm({...form,role_name:e.target.value})} placeholder="e.g. Coach" style={inputSt(T)}/>
              </div>
            </div>
            <div>
              <label style={labelSt(T)}>Description</label>
              <div style={inputWrapSt(T)}>
                <input value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Brief description" style={inputSt(T)}/>
              </div>
            </div>
          </div>

          <SectionLabel>Page Access Permissions</SectionLabel>

          {/* Select All row */}
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18,padding:"10px 14px",borderRadius:10,background:T.glass,border:T.glassBdr}}>
            <div onClick={toggleAll} style={{
              width:18,height:18,borderRadius:4,cursor:"pointer",flexShrink:0,
              background:allSelected?`linear-gradient(135deg,${T.yellow},#fff3a0)`:someSelected?"rgba(255,200,0,0.25)":T.divider,
              border:allSelected?"none":someSelected?`1.5px solid rgba(255,200,0,0.50)`:T.glassBdr,
              display:"flex",alignItems:"center",justifyContent:"center",
              boxShadow:allSelected?"0 2px 6px rgba(255,200,0,0.30)":"none",
            }}>
              {allSelected&&<span style={{fontSize:11,color:T.blue,fontWeight:900}}>&#10003;</span>}
              {someSelected&&<span style={{fontSize:11,color:T.blue,fontWeight:900}}>-</span>}
            </div>
            <span style={{fontSize:13,fontWeight:800,color:T.textPrimary,fontFamily:T.font}}>
              Select All Pages
            </span>
            <span style={{marginLeft:"auto",fontSize:12,color:T.textMuted}}>
              {form.permissions.length} / {ALL_PAGES.length} selected
            </span>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:20,marginBottom:24}}>
            {PAGE_GROUPS.map(grp=>{
              const allOn=grp.pages.every(p=>form.permissions.includes(p));
              const someOn=grp.pages.some(p=>form.permissions.includes(p));
              const Ic = grp.icon;
              return(
                <div key={grp.label} style={{borderRadius:12,border:T.glassBdr,overflow:"hidden"}}>
                  {/* Group header */}
                  <div style={{
                    display:"flex",alignItems:"center",gap:10,padding:"10px 16px",
                    background:`linear-gradient(110deg,${grp.color}28,${grp.color}14)`,
                    borderBottom:T.divider,
                  }}>
                    <div onClick={()=>toggleGroup(grp.pages)} style={{
                      width:18,height:18,borderRadius:4,cursor:"pointer",flexShrink:0,
                      background:allOn?`linear-gradient(135deg,${T.yellow},#fff3a0)`:someOn?"rgba(255,200,0,0.25)":T.divider,
                      border:allOn?"none":someOn?`1.5px solid rgba(255,200,0,0.50)`:T.glassBdr,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      boxShadow:allOn?"0 2px 6px rgba(255,200,0,0.30)":"none",
                    }}>
                      {allOn&&<span style={{fontSize:11,color:T.blue,fontWeight:900}}>&#10003;</span>}
                      {someOn&&!allOn&&<span style={{fontSize:11,color:T.blue,fontWeight:900}}>-</span>}
                    </div>
                    <div style={{width:26,height:26,borderRadius:7,background:`${grp.color}22`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <Ic size={12} color={grp.color}/>
                    </div>
                    <span style={{fontSize:13,fontWeight:800,color:T.textPrimary,letterSpacing:"0.03em",fontFamily:T.font}}>
                      {grp.label}
                    </span>
                    <span style={{marginLeft:"auto",fontSize:11,color:T.textSub,padding:"2px 8px",borderRadius:10,background:T.divider,border:T.glassBdr}}>
                      {grp.pages.filter(p=>form.permissions.includes(p)).length}/{grp.pages.length}
                    </span>
                  </div>
                  {/* Page tiles */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:8,padding:12,background:T.glass}}>
                    {grp.pages.map(pg=>{
                      const on=form.permissions.includes(pg);
                      return(
                        <div key={pg} onClick={()=>toggle(pg)} style={{
                          display:"flex",alignItems:"center",gap:10,
                          padding:"9px 14px",borderRadius:10,cursor:"pointer",
                          background:on?"rgba(255,200,0,0.14)":T.rowOdd,
                          border:on?"1.5px solid rgba(255,200,0,0.40)":T.glassBdr,
                          transition:"all 0.15s",
                        }}>
                          <div style={{
                            width:15,height:15,borderRadius:3,flexShrink:0,
                            background:on?`linear-gradient(135deg,${T.yellow},#fff3a0)`:T.divider,
                            border:on?"none":T.glassBdr,
                            display:"flex",alignItems:"center",justifyContent:"center",
                            boxShadow:on?"0 2px 6px rgba(255,200,0,0.28)":"none",
                          }}>
                            {on&&<span style={{fontSize:10,color:T.blue,fontWeight:900}}>&#10003;</span>}
                          </div>
                          <span style={{fontSize:13,fontWeight:600,color:on?T.textPrimary:T.textSub,fontFamily:T.font}}>
                            {pg}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Permission summary */}
          <div style={{padding:"10px 14px",borderRadius:10,background:T.glass,border:T.glassBdr,marginBottom:22}}>
            <span style={{fontSize:12,fontWeight:700,color:T.textPrimary,fontFamily:T.font}}>
              {form.permissions.length} page{form.permissions.length!==1?"s":""} selected
            </span>
            {form.permissions.length>0&&(
              <span style={{fontSize:12,color:"#8899aa",marginLeft:8,fontFamily:T.font}}>
                ({form.permissions.join(", ")})
              </span>
            )}
          </div>

          <div style={{display:"flex",gap:14}}>
            <button onClick={save} style={{...yellowBtn(T),flex:1,padding:13}}><FaSave size={15}/> Save Role</button>
            <button onClick={()=>setShowForm(false)} style={{...blueBtn(T),flex:1,padding:13}}><FaTimesCircle size={15}/> Cancel</button>
          </div>
        </div>
      )}

      {/* ── Roles table ── */}
      <div style={{background:T.glass,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",border:T.glassBdr,borderRadius:14,boxShadow:T.glassSh,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,tableLayout:"fixed"}}>
          <colgroup>
            <col style={{width:"5%"}}/><col style={{width:"15%"}}/><col style={{width:"22%"}}/>
            <col style={{width:"38%"}}/><col style={{width:"10%"}}/><col style={{width:"10%"}}/>
          </colgroup>
          <thead>
            <tr style={{background:T.divider}}>
              {["#","Role","Description","Pages","Edit","Delete"].map(h=>(
                <th key={h} style={thSt}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roles.length===0?(
              <tr><td colSpan={6} style={{...tdSt,textAlign:"center",padding:36,color:T.textMuted}}>No roles defined yet — click New Role to create one</td></tr>
            ):roles.map((r,idx)=>(
              <tr key={r.role_id}
                style={{background:idx%2===0?T.rowEven:T.rowOdd}}
                onMouseEnter={e=>(e.currentTarget.style.background="rgba(255,200,0,0.08)")}
                onMouseLeave={e=>(e.currentTarget.style.background=idx%2===0?T.rowEven:T.rowOdd)}>
                <td style={{...tdSt,color:T.textMuted,fontWeight:600}}>{r.role_id}</td>
                <td style={{...tdSt,fontWeight:700,color:T.textPrimary}}>{r.role_name}</td>
                <td style={{...tdSt,color:T.textSub}}>{r.description||"—"}</td>
                <td style={tdSt}>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                    {(r.permissions||[]).slice(0,5).map(p=>(
                      <span key={p} style={{padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:600,background:T.glass,color:T.textSub,border:T.glassBdr,fontFamily:T.font}}>{p}</span>
                    ))}
                    {(r.permissions||[]).length>5&&(
                      <span style={{padding:"2px 8px",borderRadius:20,fontSize:11,color:T.textMuted,fontWeight:600}}>
                        +{r.permissions.length-5} more
                      </span>
                    )}
                  </div>
                </td>
                <td style={tdSt}>
                  <button onClick={()=>openEdit(r)} style={{background:"none",border:"none",cursor:"pointer",color:T.textPrimary,fontWeight:700,fontSize:13,fontFamily:T.font,display:"flex",alignItems:"center",gap:5}}>
                    <FaEdit size={13}/> Edit
                  </button>
                </td>
                <td style={tdSt}>
                  <button onClick={()=>deleteRole(r)} style={{background:"none",border:"none",cursor:"pointer",color:"#c0392b",fontWeight:700,fontSize:13,fontFamily:T.font,display:"flex",alignItems:"center",gap:5}}>
                    <FaTrash size={12}/> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
